import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import EditableTable from '../../../../../components/EditableTable/EditableTable';
import {
    GridCellParams,
    GridColDef,
    GridEditSingleSelectCell,
    GridPreProcessEditCellProps,
    GridRenderEditCellParams,
    useGridApiContext,
    useGridApiRef,
} from '@mui/x-data-grid';
import DisplayLogicSwitcherColumn from '../../../../../components/EditableTable/CustomColumns/DisplayLogicSwitcherColumn';
import { NumberInputColumn } from '../../../../../components/EditableTable/CustomColumns/NumberInputColumn/NumberInputColumn';
import { LegalIssueItem } from '../../../../../slices/marketplace/loi-form/legal-issue-item';
import { LoiFormSection } from '../../../../../slices/marketplace/loi-form/loi-form-section';
import { LoiForm } from '../../../../../slices/marketplace/loi-form/loi-form';
import { LegalIssueType } from '../../../../../slices/marketplace/loi-form/legal-issue-type.enum';
import {
    NonCompetitionRestrictionSlot,
    NonCompetitionRestrictionSlotOptions,
    NonCompetitionRestrictionSlotLabels,
} from '../../../../../slices/marketplace/loi-form/non-competition-restriction-slot';
import {
    LoiFormAnswerType,
    LoiFormAnswerTypeList,
} from '../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { getOrderedItemsForSection } from '../../../../../slices/marketplace/loi-form/get-ordered-items-for-section';
import { wouldCreateCycle } from '../../../../../slices/marketplace/loi-form/legal-issue-visibility';
import { generateGuid } from '../../../../../utils/typedHelper';
import { EditorTypeEnum } from '../../../../../models/DataRequestHub/ProjectEditorEnum';
import { CustomIdConstants } from '../../../../../models/DataRequestHub/CustomIdConstants';
import TextInputColumn from '../../../../../components/EditableTable/CustomColumns/TextInputColumn/TextInputColumn';
import {
    MultiSelectEditCell,
    MultiSelectOption,
} from '../../../../../components/EditableTable/CustomColumns/MultiselectInputColumn/MultiselectInputColumn';
import CustomRenderCell from '../../../../../components/EditableTable/CustomRenderCell/CustomRenderCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import ReactSwitch from 'react-switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { LoiFormSectionEditorModal } from '../LoiFormSectionEditorModal/LoiFormSectionEditorModal';
import {
    validateLoiFormItemDisplayOrder,
    validateRequiredCell,
    removeErrorClassFromCellContent,
} from '../../../../../components/EditableTable/EditableTableValidationHelper';
import type { LoiFormItemDisplayOrderValidationResult } from '../../../../../components/EditableTable/EditableTableValidationHelper';
import {
    applySparseDisplayOrderInsert,
    resolveDisplayOrderDuplicates,
} from './displayOrderHelpers';
import './LegalIssuesGridEditor.scss';

const IMPLEMENTED_ANSWER_TYPES = LoiFormAnswerTypeList.filter(
    (d) => d.isImplemented !== false
);
const ANSWER_TYPE_LABELS = IMPLEMENTED_ANSWER_TYPES.map((d) => d.name);

const SELECT_ANSWER_TYPES = new Set([
    LoiFormAnswerType.PurchaseAgreementType,
    LoiFormAnswerType.LeaseRateUponExtension,
]);

export interface LegalIssuesGridEditorProps {
    formData: LoiForm;
    onFormChange: (payload: LoiForm) => void;
    legalIssueType: LegalIssueType;
}

export interface LegalIssuesGridEditorHandle {
    validate: () => boolean;
    addRow: () => void;
}

export const LegalIssuesGridEditor = forwardRef<
    LegalIssuesGridEditorHandle,
    LegalIssuesGridEditorProps
>((props, ref) => {
    const apiRef = useGridApiRef();
    const [isSectionModalVisible, setIsSectionModalVisible] = useState(false);

    const items =
        props.legalIssueType === LegalIssueType.LegalIssue
            ? (props.formData.legalIssues ?? [])
            : (props.formData.additionalCovenants ?? []);
    const sections = props.formData.legalIssueSections ?? [];
    const sectionsRef = useRef(sections);
    sectionsRef.current = sections;

    const sortedRows = useMemo(() => {
        const sectionsOrdered = [...sections].sort((a, b) => a.order - b.order);
        const sectionIds = new Set(sectionsOrdered.map((s) => s.id));
        const inSection = items.filter(
            (i) => i.sectionId && sectionIds.has(i.sectionId)
        );
        const noSection = items.filter(
            (i) => !i.sectionId || !sectionIds.has(i.sectionId!)
        );
        const ordered = sectionsOrdered.flatMap((s) =>
            getOrderedItemsForSection(s.id, inSection)
        );
        return [...ordered, ...noSection];
    }, [items, sections]);

    const gridRows = useMemo(
        () =>
            sortedRows.map((row) => {
                if (
                    SELECT_ANSWER_TYPES.has(row.answerType) &&
                    (!row.options || row.options.length === 0)
                ) {
                    const def = LoiFormAnswerTypeList.find(
                        (d) => d.id === row.answerType
                    );
                    return def?.options
                        ? { ...row, options: def.options }
                        : row;
                }
                return row;
            }),
        [sortedRows]
    );

    const ensureUniqueNcrSlot = (
        prevRows: LegalIssueItem[],
        nextRows: LegalIssueItem[]
    ): LegalIssueItem[] => {
        const changedRow = nextRows.find((nr) => {
            const pr = prevRows.find((r) => r.id === nr.id);
            return (
                pr &&
                nr.linkedNonCompetitionRestrictionSlot !==
                    pr.linkedNonCompetitionRestrictionSlot
            );
        });
        const newSlot = changedRow?.linkedNonCompetitionRestrictionSlot;
        if (newSlot == null) return nextRows;
        return nextRows.map((r) =>
            r.id !== changedRow!.id &&
            r.linkedNonCompetitionRestrictionSlot === newSlot
                ? { ...r, linkedNonCompetitionRestrictionSlot: null }
                : r
        );
    };

    const applyItemsWithNcrUniqueness = (
        form: LoiForm,
        nextItems: LegalIssueItem[],
        prevItems: LegalIssueItem[],
        legalIssueType: LegalIssueType
    ): LoiForm => {
        const changedRow = nextItems.find((nr) => {
            const pr = prevItems.find((r) => r.id === nr.id);
            return (
                pr &&
                nr.linkedNonCompetitionRestrictionSlot !==
                    pr.linkedNonCompetitionRestrictionSlot
            );
        });
        const newSlot = changedRow?.linkedNonCompetitionRestrictionSlot;
        const currentArrayKey =
            legalIssueType === LegalIssueType.LegalIssue
                ? 'legalIssues'
                : 'additionalCovenants';
        const baseForm = { ...form, [currentArrayKey]: nextItems };
        if (newSlot == null) return baseForm;
        const otherArray =
            currentArrayKey === 'legalIssues'
                ? (form.additionalCovenants ?? [])
                : (form.legalIssues ?? []);
        const cleared = otherArray.map((r) =>
            r.linkedNonCompetitionRestrictionSlot === newSlot
                ? { ...r, linkedNonCompetitionRestrictionSlot: null }
                : r
        );
        return currentArrayKey === 'legalIssues'
            ? { ...baseForm, additionalCovenants: cleared }
            : { ...baseForm, legalIssues: cleared };
    };

    const setItems = useCallback(
        (nextItems: LegalIssueItem[], prevItems: LegalIssueItem[]) => {
            props.onFormChange(
                applyItemsWithNcrUniqueness(
                    props.formData,
                    nextItems,
                    prevItems,
                    props.legalIssueType
                )
            );
        },
        [props.formData, props.legalIssueType, props.onFormChange]
    );

    const setSections = useCallback(
        (nextSections: LoiFormSection[]) => {
            const nextSectionIds = new Set(nextSections.map((s) => s.id));
            const currentItems =
                props.legalIssueType === LegalIssueType.LegalIssue
                    ? (props.formData.legalIssues ?? [])
                    : (props.formData.additionalCovenants ?? []);

            const updatedItems = currentItems.map((item) =>
                item.sectionId && !nextSectionIds.has(item.sectionId)
                    ? { ...item, sectionId: null }
                    : item
            );

            const itemsKey =
                props.legalIssueType === LegalIssueType.LegalIssue
                    ? 'legalIssues'
                    : 'additionalCovenants';

            props.onFormChange({
                ...props.formData,
                legalIssueSections: nextSections,
                [itemsKey]: updatedItems,
            });
        },
        [props.formData, props.onFormChange, props.legalIssueType]
    );

    const getDefaultRow = useCallback((): LegalIssueItem => {
        return {
            id: generateGuid(),
            name: '',
            value: null,
            guidance: null,
            tooltip: null,
            answerType: LoiFormAnswerType.String,
            sectionId: null,
            displayOrder: null,
            legalIssueType: props.legalIssueType,
            showValueInput: false,
            linkedNonCompetitionRestrictionSlot: null,
            customId: null,
            isDisplayLogicEnabled: false,
            logicParentCustomId: null,
            conditionValues: [],
        };
    }, [props.legalIssueType]);

    const setRowsForGrid = useCallback(
        (fn: (oldRows: any[]) => any[]) => {
            const latestRows = Array.from(
                apiRef.current?.getRowModels()?.values?.() ?? []
            ) as any[];
            const prevRows = latestRows.length > 0 ? latestRows : gridRows;

            const nextGridRows = fn(prevRows);

            const addedRow = nextGridRows.find(
                (newRow) => !prevRows.some((r) => r.id === newRow.id)
            );

            const changedRow = !addedRow
                ? nextGridRows.find((newRow) => {
                      const oldRow = prevRows.find((r) => r.id === newRow.id);
                      return (
                          oldRow && newRow.displayOrder !== oldRow.displayOrder
                      );
                  })
                : undefined;

            const sectionChangedRow = !addedRow
                ? nextGridRows.find((newRow) => {
                      const oldRow = prevRows.find((r) => r.id === newRow.id);
                      return (
                          oldRow && newRow.sectionId !== oldRow.sectionId
                      );
                  })
                : undefined;

            const insertRow = changedRow ?? addedRow ?? sectionChangedRow;
            const insertOrder: number | undefined = insertRow?.displayOrder;

            let finalRows: any[];

            if (insertRow && insertOrder != null && insertOrder >= 1) {
                finalRows = applySparseDisplayOrderInsert(nextGridRows, insertRow);
            } else if (sectionChangedRow) {
                finalRows = resolveDisplayOrderDuplicates(nextGridRows);
            } else {
                finalRows = nextGridRows;
            }

            finalRows = resolveDisplayOrderDuplicates(finalRows);
            finalRows = ensureUniqueNcrSlot(
                prevRows as LegalIssueItem[],
                finalRows as LegalIssueItem[]
            );

            const nextItems: LegalIssueItem[] = finalRows.map((row: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { isNew, ...rest } = row;
                return rest as LegalIssueItem;
            });
            setItems(nextItems, prevRows as LegalIssueItem[]);
        },
        [gridRows, setItems]
    );

    const sectionOptions = sections.map((s) => ({
        value: s.id,
        label: s.name,
    }));

    const getConditionValueOptions = (
        rows: LegalIssueItem[],
        logicParentCustomId: string | null
    ): MultiSelectOption[] => {
        if (!logicParentCustomId) return [];
        const parent = rows.find((r) => r.customId === logicParentCustomId);
        if (!parent) return [];
        if (SELECT_ANSWER_TYPES.has(parent.answerType)) {
            return (parent.options ?? []).map((o) => ({ id: 0, data: o.label }));
        }
        return [];
    };

    const handleReferencedIssueChange = (
        id: string,
        newParentCustomId: string
    ) => {
        const latestRows = Array.from(
            apiRef.current.getRowModels().values()
        ) as LegalIssueItem[];
        const updated = latestRows.map((r) =>
            r.id === id
                ? { ...r, logicParentCustomId: newParentCustomId, conditionValues: [] }
                : r
        );
        setRowsForGrid(() => updated);
    };

    const NcrSlotEditCell = (params: GridRenderEditCellParams & { setRowsForGrid: (fn: (oldRows: any[]) => any[]) => void }) => {
        const apiRef = useGridApiContext();
        const rowId = params.id;
        const currentValue = params.value;
        const handleChange = (event: { target: { value: unknown } }) => {
            const raw = event.target.value;
            const newValue =
                raw === '' || raw == null
                    ? null
                    : (Number(raw) as NonCompetitionRestrictionSlot);
            params.setRowsForGrid((prevRows: any[]) =>
                prevRows.map((r: any) =>
                    r.id === rowId
                        ? { ...r, linkedNonCompetitionRestrictionSlot: newValue }
                        : r
                )
            );
            apiRef.current.stopCellEditMode({
                id: rowId,
                field: params.field,
                ignoreModifications: true,
            });
        };
        const value = currentValue != null ? String(currentValue) : '';
        return (
            <Select
                value={value}
                onChange={handleChange}
                size="small"
                fullWidth
                displayEmpty
                autoFocus
                sx={{ width: '100%', minHeight: '100%' }}
            >
                <MenuItem value="">{'\u00A0'}</MenuItem>
                {NonCompetitionRestrictionSlotOptions.map((o) => (
                    <MenuItem key={o.value} value={String(o.value)}>
                        {o.label}
                    </MenuItem>
                ))}
            </Select>
        );
    };

    const displayOrderColumn: GridColDef = {
        field: 'displayOrder',
        headerName: 'Display Order',
        minWidth: 110,
        flex: 0.3,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'cell-input display-order',
        valueSetter: (value, row) => {
            const num = value === '' || value == null ? 0 : Number(value);
            return { ...row, displayOrder: num };
        },
        editable: true,
        renderCell: (params) => (
            <CustomRenderCell
                {...params}
                anchorTooltipId={`display-order-${params.id}`}
                renderValue={params.value ?? ''}
            />
        ),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const v = params.props.value;
            const hasError = v == null || (typeof v === 'number' && v < 1);
            return { ...params.props, error: hasError };
        },
    };

    const handleShowValueInputChange = (rowId: string, value: boolean) => {
        const latest = Array.from(
            apiRef.current.getRowModels().values()
        ) as any[];
        const updated = latest.map((r) =>
            r.id === rowId
                ? {
                      ...r,
                      showValueInput: value,
                      ...(value
                          ? {
                                answerType: LoiFormAnswerType.String,
                                options: undefined,
                                customId: CustomIdConstants.getNextLegalIssueCustomId(latest),
                            }
                          : {
                                customId: null,
                                isDisplayLogicEnabled: false,
                                logicParentCustomId: null,
                                conditionValues: [],
                            }),
                  }
                : r
        );
        setRowsForGrid(() => updated);
        apiRef.current.forceUpdate();
    };

    const showValueInputSwitcherColumn: GridColDef = {
        field: 'showValueInput',
        headerName: 'Input',
        minWidth: 70,
        maxWidth: 90,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'switcher-cell',
        sortable: false,
        renderCell: (params) => {
            const row = params.row as LegalIssueItem;
            const checked = row.showValueInput !== false;
            return (
                <ReactSwitch
                    offColor="#d92550"
                    checked={!!checked}
                    onChange={(value) => handleShowValueInputChange(row.id, value)}
                    height={22}
                />
            );
        },
    };

    const columns: GridColDef[] = [
        displayOrderColumn,
        {
            field: 'customId',
            headerName: 'Legal Item ID',
            minWidth: 110,
            flex: 0.3,
            align: 'left',
            headerAlign: 'left',
            cellClassName: (params) => {
                const row = params.row as LegalIssueItem;
                return row.showValueInput !== false
                    ? 'cell-text legal-item-id'
                    : 'cell-text legal-item-id disabled';
            },
            editable: false,
            sortComparator: (a: string, b: string) => {
                const numA = parseInt((a ?? '').replace('LI', ''), 10) || 0;
                const numB = parseInt((b ?? '').replace('LI', ''), 10) || 0;
                return numA - numB;
            },
        },
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 160,
            flex: 0.6,
            align: 'left',
            headerAlign: 'left',
            cellClassName: 'cell-text-input name',
            editable: true,
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`name-${params.id}`}
                    renderValue={params.value}
                />
            ),
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !params.props.value?.trim?.();
                return { ...params.props, error: hasError };
            },
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        {
            field: 'sectionId',
            headerName: 'Section',
            minWidth: 160,
            flex: 0.5,
            align: 'left',
            headerAlign: 'left',
            cellClassName: 'section-id',
            type: 'singleSelect',
            valueOptions: sectionOptions,
            valueGetter: (value) => value ?? '',
            valueSetter: (value, row) => ({
                ...row,
                sectionId: value === '' || value == null ? null : value,
            }),
            renderHeader: () => (
                <>
                    Section
                    <FontAwesomeIcon
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsSectionModalVisible(true);
                        }}
                        style={{ marginLeft: '10px' }}
                        size="1x"
                        icon={faEdit as any}
                        title="Manage sections"
                    />
                </>
            ),
            renderCell: (params) => {
                const opt = sectionOptions.find(
                    (o) => o.value === params.value
                );
                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={`section-${params.id}`}
                        renderValue={opt?.label ?? params.value ?? ''}
                    />
                );
            },
            editable: true,
        },
        {
            field: 'headerLevel',
            headerName: 'Header Level',
            minWidth: 100,
            flex: 0.35,
            align: 'left',
            headerAlign: 'left',
            cellClassName: 'cell-input header-level',
            type: 'number',
            valueGetter: (value) => value ?? '',
            valueSetter: (value, row) => ({
                ...row,
                headerLevel: value === '' || value == null ? null : Number(value),
            }),
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`header-${params.id}`}
                    renderValue={params.value ?? ''}
                />
            ),
            renderEditCell: (params) => <NumberInputColumn {...params} />,
            editable: true,
        },
        showValueInputSwitcherColumn,
        {
            field: 'answerType',
            headerName: 'Answer Type',
            minWidth: 110,
            flex: 0.4,
            align: 'left',
            headerAlign: 'left',
            type: 'singleSelect',
            valueOptions: ANSWER_TYPE_LABELS,
            valueGetter: (value) => {
                if (value == null) return 'String';
                const def = LoiFormAnswerTypeList.find((d) => d.id === value);
                return def?.name ?? 'String';
            },
            valueSetter: (value, row) => {
                if (value === '' || value == null) {
                    return {
                        ...row,
                        answerType: LoiFormAnswerType.String,
                        options: undefined,
                    };
                }
                const def = LoiFormAnswerTypeList.find((d) => d.name === value);
                return {
                    ...row,
                    answerType: def?.id ?? LoiFormAnswerType.String,
                    options: def?.options,
                };
            },
            renderCell: (params) => {
                const row = params.row as LegalIssueItem;
                const isDisabled = row.showValueInput === false;
                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={`answer-${params.id}`}
                        renderValue={
                            isDisabled
                                ? ''
                                : (params.formattedValue ?? 'String')
                        }
                    />
                );
            },
            cellClassName: (params) =>
                `answer-type${(params.row as LegalIssueItem).showValueInput !== false ? '' : ' disabled'}`,
            editable: true,
        },
        {
            field: 'value',
            headerName: 'Default Value',
            minWidth: 110,
            flex: 0.4,
            align: 'left',
            headerAlign: 'left',
            cellClassName: (params) =>
                (params.row as LegalIssueItem).showValueInput !== false
                    ? 'cell-text-input value'
                    : 'disabled',
            editable: ((params: any) =>
                (params.row as LegalIssueItem).showValueInput !==
                false) as unknown as boolean,
            renderCell: (params) => {
                const row = params.row as LegalIssueItem;
                const isDisabled = row.showValueInput === false;
                const isSelectType = SELECT_ANSWER_TYPES.has(row.answerType);
                const displayValue = isSelectType
                    ? ((row.options ?? []).find((o) => o.value === row.value)
                          ?.label ??
                      row.value ??
                      '')
                    : (row.value ?? '');
                return (
                    <CustomRenderCell
                        {...params}
                        anchorTooltipId={`value-${params.id}`}
                        renderValue={isDisabled ? '' : displayValue}
                    />
                );
            },
            type: 'singleSelect',
            valueOptions: (params) => {
                const row = params.row as LegalIssueItem;
                return SELECT_ANSWER_TYPES.has(row?.answerType)
                    ? (row.options ?? []).map((o) => ({
                          value: o.value,
                          label: o.label,
                      }))
                    : [];
            },
            renderEditCell: (params) => {
                const row = params.row as LegalIssueItem;
                if (SELECT_ANSWER_TYPES.has(row.answerType)) {
                    return <GridEditSingleSelectCell {...params} />;
                }
                if (row.answerType === LoiFormAnswerType.Number) {
                    return <NumberInputColumn {...params} />;
                }
                return <TextInputColumn {...params} />;
            },
        },
        {
            field: 'guidance',
            headerName: 'Guidance',
            minWidth: 140,
            flex: 0.5,
            align: 'left',
            headerAlign: 'left',
            cellClassName: 'cell-text-input guidance',
            editable: true,
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`guidance-${params.id}`}
                    renderValue={params.value ?? ''}
                />
            ),
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        {
            field: 'tooltip',
            headerName: 'Tooltip',
            minWidth: 120,
            flex: 0.4,
            align: 'left',
            headerAlign: 'left',
            cellClassName: 'cell-text-input tooltip-text',
            editable: true,
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`tooltip-${params.id}`}
                    renderValue={params.value ?? ''}
                />
            ),
            renderEditCell: (params) => <TextInputColumn {...params} />,
        },
        ...(props.legalIssueType === LegalIssueType.LegalIssue ||
        props.legalIssueType === LegalIssueType.AdditionalCovenant
            ? [
                  {
                      field: 'linkedNonCompetitionRestrictionSlot',
                      headerName: 'Non-Competition Link',
                      minWidth: 180,
                      flex: 0.5,
                      align: 'left' as const,
                      headerAlign: 'left' as const,
                      cellClassName: 'ncr-slot',
                      type: 'singleSelect' as const,
                      valueOptions: [
                          { value: '', label: '' },
                          ...NonCompetitionRestrictionSlotOptions.map((o) => ({
                              value: String(o.value),
                              label: o.label,
                          })),
                      ],
                      valueGetter: (value: NonCompetitionRestrictionSlot | null | undefined) =>
                          value != null ? String(value) : '',
                      valueSetter: (value: string, row: LegalIssueItem) => ({
                          ...row,
                          linkedNonCompetitionRestrictionSlot:
                              value === '' || value == null
                                  ? null
                                  : (Number(value) as NonCompetitionRestrictionSlot),
                      }),
                      renderCell: (params: any) => {
                          const v = params.value;
                          const label =
                              v !== '' && v != null && Number(v) in NonCompetitionRestrictionSlotLabels
                                  ? NonCompetitionRestrictionSlotLabels[Number(v) as NonCompetitionRestrictionSlot]
                                  : '';
                          return (
                              <CustomRenderCell
                                  {...params}
                                  anchorTooltipId={`ncr-slot-${params.id}`}
                                  renderValue={label}
                              />
                          );
                      },
                      renderEditCell: (params: GridRenderEditCellParams) => (
                          <NcrSlotEditCell {...params} setRowsForGrid={setRowsForGrid} />
                      ),
                      editable: (params: GridCellParams) =>
                          gridRows.length > 0 && params.row.id === gridRows[gridRows.length - 1].id,
                  } as unknown as GridColDef,
              ]
            : []),
        DisplayLogicSwitcherColumn({
            setRows: setRowsForGrid,
            gridApiRef: apiRef,
            getRowId: (row) => (row as LegalIssueItem).id,
        }),
        {
            field: 'logicParentCustomId',
            headerName: 'Reference Issue ID',
            minWidth: 200,
            flex: 0.5,
            align: 'left',
            headerAlign: 'left',
            type: 'singleSelect',
            cellClassName: (params) => {
                const row = params.row as LegalIssueItem;
                return row.isDisplayLogicEnabled
                    ? 'cell-text-input logic-parent-custom-id'
                    : 'cell-text-input logic-parent-custom-id disabled';
            },
            valueOptions: (params) => {
                const row = params.row as LegalIssueItem;
                const latestRows = Array.from(
                    apiRef.current.getRowModels().values()
                ) as LegalIssueItem[];
                return latestRows
                    .filter((r) => r.id !== row.id && r.customId)
                    .filter((r) => !row.customId || !wouldCreateCycle(latestRows, row.customId, r.customId as string))
                    .sort((a, b) => {
                        const numA = parseInt(a.customId.replace('LI', ''), 10);
                        const numB = parseInt(b.customId.replace('LI', ''), 10);
                        return numA - numB;
                    })
                    .map((r) => ({
                        value: r.customId as string,
                        label: `${r.customId} — ${r.name}`,
                    }));
            },
            valueGetter: (value) => value ?? '',
            valueSetter: (value, row) => ({
                ...row,
                logicParentCustomId: value === '' || value == null ? null : value,
                conditionValues: [],
            }),
            editable: true,
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`logic-parent-${params.id}`}
                    renderValue={params.value ?? ''}
                />
            ),
            renderEditCell: (params) => {
                const row = params.row as LegalIssueItem;
                if (!row.isDisplayLogicEnabled) return <></>;
                return (
                    <GridEditSingleSelectCell
                        onValueChange={(_event, newValue) =>
                            handleReferencedIssueChange(
                                row.id,
                                newValue as string
                            )
                        }
                        {...params}
                    />
                );
            },
        } as GridColDef,
        {
            field: 'conditionValues',
            headerName: 'Condition Value',
            minWidth: 160,
            flex: 0.4,
            align: 'left',
            headerAlign: 'left',
            cellClassName: (params) => {
                const row = params.row as LegalIssueItem;
                const isDisabled = !row.isDisplayLogicEnabled || !row.logicParentCustomId;
                return isDisabled
                    ? 'cell-text-input condition-value disabled'
                    : 'cell-text-input condition-value';
            },
            valueGetter: (value: string[] | null | undefined) => value ?? [],
            valueSetter: (value, row) => {
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                    const strings = (value as MultiSelectOption[]).map((o) => o.data).filter(Boolean);
                    return { ...row, conditionValues: strings };
                }
                if (typeof value === 'string') {
                    return { ...row, conditionValues: value === '' ? [] : [value] };
                }
                return { ...row, conditionValues: [] };
            },
            editable: true,
            renderCell: (params) => (
                <CustomRenderCell
                    {...params}
                    anchorTooltipId={`condition-value-${params.id}`}
                    renderValue={
                        Array.isArray(params.row.conditionValues)
                            ? params.row.conditionValues.join(', ')
                            : ''
                    }
                />
            ),
            renderEditCell: (params) => {
                const row = params.row as LegalIssueItem;
                if (!row.isDisplayLogicEnabled || !row.logicParentCustomId) return <></>;
                const latestRows = Array.from(
                    apiRef.current.getRowModels().values()
                ) as LegalIssueItem[];
                const options = getConditionValueOptions(latestRows, row.logicParentCustomId);
                if (options.length > 0) {
                    return MultiSelectEditCell(params, options);
                }
                return (
                    <TextInputColumn
                        {...params}
                        value={
                            Array.isArray(params.value)
                                ? (params.value[0] ?? '')
                                : (params.value ?? '')
                        }
                    />
                );
            },
        } as GridColDef,
    ];

    const validateAllRows = useCallback(() => {
        const currentRows = Array.from(
            apiRef.current?.getRowModels()?.values() ?? []
        ) as any[];
        const gridState = apiRef.current?.state;
        const displayOrderResult: LoiFormItemDisplayOrderValidationResult =
            validateLoiFormItemDisplayOrder(currentRows, gridState as any);
        const editRows = (gridState as any)?.editRows;
        const isEditing = editRows && Object.keys(editRows).length > 0;
        const validSectionIds = new Set(
            sectionsRef.current.map((s) => s.id)
        );

        const isValueEmpty = (value: any) =>
            !value?.toString()?.trim();

        const isSectionValid = (sectionId: string | null) =>
            !!sectionId && validSectionIds.has(sectionId);

        const isHeaderLevelValid = (headerLevel: number | undefined | null) =>
            headerLevel != null && headerLevel >= 1;

        // Data-level validation: checks ALL rows regardless of DOM/pagination
        const isConditionalityValid = (row: any) => {
            if (!row.isDisplayLogicEnabled) return true;
            if (!row.logicParentCustomId) return false;
            if (!Array.isArray(row.conditionValues) || row.conditionValues.length === 0) return false;
            return row.conditionValues.some((v: string) => v?.trim?.());
        };

        let hasFieldErrors = false;
        currentRows.forEach((row) => {
            if (isValueEmpty(row.name)) hasFieldErrors = true;
            if (!isSectionValid(row.sectionId)) hasFieldErrors = true;
            if (!isHeaderLevelValid(row.headerLevel)) hasFieldErrors = true;
            if (!isConditionalityValid(row)) hasFieldErrors = true;
        });

        // DOM-level validation: applies visual error indicators to visible rows
        const applyVisualValidation = () => {
            currentRows.forEach((row) => {
                const element = document.querySelector(
                    `[data-id="${row.id}"]`
                );
                if (!element) return;

                validateRequiredCell(
                    element.querySelector('.name'),
                    row.name
                );
                validateRequiredCell(
                    element.querySelector('.section-id'),
                    isSectionValid(row.sectionId) ? row.sectionId : ''
                );
                validateRequiredCell(
                    element.querySelector('.header-level'),
                    isHeaderLevelValid(row.headerLevel)
                        ? String(row.headerLevel)
                        : ''
                );

                const answerTypeCell = element.querySelector('.answer-type');
                if (answerTypeCell) {
                    if (row.showValueInput !== false) {
                        validateRequiredCell(
                            answerTypeCell,
                            row.answerType != null
                                ? String(row.answerType)
                                : ''
                        );
                    } else {
                        removeErrorClassFromCellContent(answerTypeCell);
                    }
                }

                const referenceCell = element.querySelector('.logic-parent-custom-id');
                if (referenceCell) {
                    if (row.isDisplayLogicEnabled) {
                        validateRequiredCell(referenceCell, row.logicParentCustomId);
                    } else {
                        removeErrorClassFromCellContent(referenceCell);
                    }
                }

                const conditionCell = element.querySelector('.condition-value');
                if (conditionCell) {
                    const conditionEnabled = row.isDisplayLogicEnabled && row.logicParentCustomId;
                    if (conditionEnabled) {
                        const hasValue = Array.isArray(row.conditionValues)
                            && row.conditionValues.some((v: string) => v?.trim?.());
                        validateRequiredCell(conditionCell, hasValue ? 'valid' : '');
                    } else {
                        removeErrorClassFromCellContent(conditionCell);
                    }
                }
            });
        };

        applyVisualValidation();

        const allValid = displayOrderResult.valid && !hasFieldErrors;
        if (!isEditing) {
            apiRef.current?.forceUpdate();
            setTimeout(() => {
                validateLoiFormItemDisplayOrder(
                    currentRows,
                    apiRef.current?.state as any
                );
                applyVisualValidation();
            }, 0);
            if (
                !allValid &&
                displayOrderResult.firstInvalidCell &&
                apiRef.current
            ) {
                apiRef.current.setCellFocus(
                    displayOrderResult.firstInvalidCell.id,
                    displayOrderResult.firstInvalidCell.field
                );
            }
        }
        return allValid;
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            validate: () => validateAllRows(),
            addRow: () => {
                const newRow = getDefaultRow();
                setRowsForGrid((oldRows) => [
                    ...oldRows,
                    { ...newRow, isNew: true },
                ]);
            },
        }),
        [validateAllRows, getDefaultRow, setRowsForGrid]
    );

    useEffect(() => {
        if (gridRows.length > 0) {
            validateAllRows();
        }
    }, [gridRows, validateAllRows]);

    useEffect(() => {
        if (!items || items.length === 0) return;
        const resolved = resolveDisplayOrderDuplicates(items as any[]);
        const byId = new Map(items.map((i) => [i.id, i.displayOrder]));
        const changed = resolved.some(
            (r) => (r.displayOrder ?? null) !== (byId.get(r.id) ?? null)
        );
        if (changed) {
            setItems(resolved as LegalIssueItem[], items);
        }
    }, [items, sections, setItems]);

    return (
        <div className="legal-issues-grid-editor">
            <EditableTable
                editorType={EditorTypeEnum.LoiFormItem}
                columns={columns}
                rows={gridRows}
                setRows={setRowsForGrid}
                gridApiRef={apiRef}
                fieldToFocus="displayOrder"
                getDefaultRow={getDefaultRow}
                disableVisibilityColumn
                validateAllRows={validateAllRows}
            />
            {isSectionModalVisible && (
                <LoiFormSectionEditorModal
                    isVisible={isSectionModalVisible}
                    onClose={() => setIsSectionModalVisible(false)}
                    sections={sections}
                    onSectionsChange={setSections}
                />
            )}
        </div>
    );
});
